import { AlertCircle, CheckCircle2, CircleDashed, Clock, Loader, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateTargetDialog } from '@/features/memorization/create-target-dialog';
import { useTargets } from '@/features/memorization/hooks';
import { TARGET_STATUS_LABELS } from '@/features/memorization/labels';
import type { TargetStatus } from '@/features/memorization/types';
import { useSurahLookup } from '@/features/quran/hooks';
import type { SurahView } from '@/features/quran/types';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

function statusVariant(status: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'COMPLETED') {
    return 'default';
  }
  if (status === 'NEEDS_REVISION' || status === 'CANCELLED') {
    return 'destructive';
  }
  if (status === 'SUBMITTED' || status === 'PARTIALLY_ACCEPTED') {
    return 'secondary';
  }
  return 'outline';
}

function surahLabel(surah: SurahView | undefined, number: number): string {
  // The reference is cached for the whole session, but the very first
  // render can precede it - the number alone is still correct, just less
  // alive, so it is the fallback rather than a spinner per row.
  if (surah === undefined) {
    return `Surah ${String(number)}`;
  }
  return `${String(number)}. ${surah.nameLatin}`;
}

function ayahRangeText(startAyah: number, endAyah: number): string {
  if (startAyah === endAyah) {
    return `Ayat ${String(startAyah)}`;
  }
  return `Ayat ${String(startAyah)}-${String(endAyah)}`;
}

function ayahCountText(startAyah: number, endAyah: number): string {
  return `${String(endAyah - startAyah + 1)} ayat`;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * "Ditugaskan" is accurate when an ustadz assigned the target, and wrong
 * when the reader set it for themselves - nobody assigned them anything.
 * Only that one status reads differently in a personal space; every other
 * value describes the target's progress, which is identical either way, so
 * this deliberately overrides one label rather than forking the whole map.
 */
function statusLabel(status: TargetStatus, mode: 'assigned' | 'personal'): string {
  if (mode === 'personal' && status === 'ASSIGNED') {
    return 'Belum disetorkan';
  }
  return TARGET_STATUS_LABELS[status];
}

/**
 * A quick visual cue per status, at a glance, before the reader gets to the
 * text label - purely presentational (Phase 8, Pilar C: no new data, no
 * schema change, no fabricated aggregate). Colors intentionally echo
 * `statusVariant`'s grouping so the icon and the badge never disagree.
 */
function StatusIcon({ status }: { readonly status: TargetStatus }): JSX.Element {
  const className = 'h-4 w-4 shrink-0';

  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className={`${className} text-primary`} aria-hidden="true" />;
    case 'NEEDS_REVISION':
      return <AlertCircle className={`${className} text-destructive`} aria-hidden="true" />;
    case 'CANCELLED':
    case 'EXPIRED':
      return <XCircle className={`${className} text-muted-foreground`} aria-hidden="true" />;
    case 'SUBMITTED':
      return <Clock className={`${className} text-muted-foreground`} aria-hidden="true" />;
    case 'PARTIALLY_ACCEPTED':
      return <CircleDashed className={`${className} text-muted-foreground`} aria-hidden="true" />;
    case 'IN_PROGRESS':
      return <Loader className={`${className} text-muted-foreground`} aria-hidden="true" />;
    case 'ASSIGNED':
    default:
      return <CircleDashed className={`${className} text-muted-foreground`} aria-hidden="true" />;
  }
}

/**
 * "Hafalan" - the student's real memorization targets (Phase G-1, Job 13).
 * Shows the list endpoint's own `status` field (accurate at creation/
 * cancellation, may lag once submissions exist - `TargetView`'s own doc
 * comment) rather than fetching per-row live coverage, which the backend
 * has no bulk endpoint for. A target's live status is available on its own
 * detail page via the coverage endpoint.
 *
 * `mode="personal"` is the solo learner reading their own list inside
 * their own space (ADR-031): the same rows, rendered the same way, but
 * the create affordance follows `POST /memorization/personal-targets`'s
 * authorization rule - `memorization_targets:read`, since a Student never
 * holds `:manage` and ADR-031 deliberately did not widen it. The gate is
 * presentation only; the server re-checks both the permission and that
 * the organization really is the caller's personal space.
 */
export function TargetList({
  studentId,
  mode = 'assigned',
  title = 'Hafalan',
}: {
  readonly studentId: string;
  readonly mode?: 'assigned' | 'personal';
  readonly title?: string;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useTargets(studentId, page, PAGE_SIZE);
  // Both are read unconditionally (rules of hooks); `mode` selects which
  // one actually governs the button.
  const canAssignTargets = useHasCapability('memorization_targets:manage');
  const canReadTargets = useHasCapability('memorization_targets:read');
  const canCreate = mode === 'personal' ? canReadTargets : canAssignTargets;
  const surahs = useSurahLookup();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>{title}</CardTitle>
        {canCreate && (
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Tambah Target Hafalan
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat target hafalan.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada target hafalan.</p>
        )}

        {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
          <>
            <ul className="flex flex-col gap-2">
              {data.items.map((target) => (
                <li key={target.id}>
                  <Link to={`/students/${studentId}/targets/${target.id}`}>
                    <div
                      className={`flex flex-col gap-2 rounded-md border p-3 transition-colors hover:bg-secondary/50 sm:flex-row sm:items-center sm:justify-between ${
                        target.status === 'COMPLETED'
                          ? 'border-primary/30 bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <StatusIcon status={target.status} />
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm font-medium">
                              {surahLabel(surahs.get(target.surah), target.surah)}
                            </span>
                            {surahs.get(target.surah) !== undefined && (
                              <span
                                lang="ar"
                                dir="rtl"
                                className="text-sm text-muted-foreground"
                              >
                                {surahs.get(target.surah)?.nameArabic}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {ayahRangeText(target.startAyah, target.endAyah)}
                            {' · '}
                            {ayahCountText(target.startAyah, target.endAyah)}
                            {target.targetDate !== null && ` · Target ${formatDate(target.targetDate)}`}
                          </span>
                        </div>
                      </div>
                      <Badge variant={statusVariant(target.status)}>
                        {statusLabel(target.status, mode)}
                      </Badge>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {data.total_pages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => Math.max(1, current - 1));
                  }}
                >
                  Sebelumnya
                </Button>
                <span className="text-xs text-muted-foreground">
                  Halaman {data.page} dari {data.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= data.total_pages}
                  onClick={() => {
                    setPage((current) => Math.min(data.total_pages, current + 1));
                  }}
                >
                  Berikutnya
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      <CreateTargetDialog
        studentId={studentId}
        mode={mode}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </Card>
  );
}
